<template>
  <b-card no-body class="h-100">
    <b-card-header>
      <b-card-title>Игры: план / факт</b-card-title>
    </b-card-header>
    <b-card-body>
      <div v-if="!hasData" class="text-center text-muted py-4">
        Нет данных о плане/факте
      </div>
      <b-row v-else>
        <b-col lg="7" class="mb-2 mb-lg-0">
          <template v-if="last30Days">
            <h5>
              Последние 30 дней
              <small class="text-muted">{{ formatPeriod(last30Days.period) }}</small>
            </h5>
            <plan-fact-summary :summary="last30Days.summary" compact />
          </template>
          <div v-else class="text-muted">Нет данных за последние 30 дней</div>
          <vue-apex-charts
            v-if="chartWeeks.length"
            type="bar"
            height="240"
            :options="chartOptions"
            :series="series"
          />
        </b-col>
        <b-col lg="5">
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Неделя</th>
                  <th>План</th>
                  <th>Факт</th>
                  <th>Конверсия</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="w in weeks" :key="w.week_id" :class="{ 'font-weight-bold': w.is_current }">
                  <td>
                    {{ shortWeekName(w.week_name) }}
                    <b-badge v-if="w.is_current" variant="light-primary" class="ml-25">текущая</b-badge>
                  </td>
                  <td>{{ w.summary && w.summary.planned_total != null ? w.summary.planned_total : '—' }}</td>
                  <td>{{ w.summary && w.summary.held != null ? w.summary.held : '—' }}</td>
                  <td>{{ formatConversion(w.summary && w.summary.conversion) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="!weeks.length" class="text-muted">Нет данных</div>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardTitle, BCardBody, BRow, BCol, BBadge,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { computed } from '@vue/composition-api'
import { $themeColors } from '@themeConfig'
import PlanFactSummary from '@/views/game/components/PlanFactSummary.vue'
import { formatConversion } from '@/views/game/planFact'

// Название недели из Planfix — «Игровая неделя 21-26 августа»; в подписях оставляем только даты
const shortWeekName = name => (name || '').replace(/^Игровая неделя\s*/i, '') || '—'

const formatShortDate = ymd => {
  if (!ymd) return '—'
  return new Date(`${ymd}T00:00:00`).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

const formatPeriod = period => {
  if (!period) return ''
  return `${formatShortDate(period.from)} — ${formatShortDate(period.to)}`
}

export default {
  components: {
    BCard, BCardHeader, BCardTitle, BCardBody, BRow, BCol, BBadge, VueApexCharts, PlanFactSummary,
  },
  props: {
    planFact: { type: Object, default: null },
  },
  setup(props) {
    // Не называем биндинг `planFact` — @vue/composition-api не проксирует на инстанс
    // setup-значение с тем же именем, что и уже объявленный prop (см. DashboardWeeklyChart.vue)
    const safePlanFact = computed(() => props.planFact || null)
    const last30Days = computed(() => (safePlanFact.value && safePlanFact.value.last_30_days) || null)
    // бэкенд отдаёт недели от новых к старым — так и показываем в таблице
    const weeks = computed(() => (safePlanFact.value && safePlanFact.value.weeks) || [])
    const hasData = computed(() => Boolean(last30Days.value) || weeks.value.length > 0)

    // на графике — хронологически, от старых к новым
    const chartWeeks = computed(() => weeks.value.slice().reverse())

    const summaryValue = (week, key) => (week.summary && week.summary[key]) || 0

    const series = computed(() => [
      { name: 'Проведено', data: chartWeeks.value.map(w => summaryValue(w, 'held')) },
      { name: 'Запланировано', data: chartWeeks.value.map(w => summaryValue(w, 'planned')) },
      { name: 'Не состоялось', data: chartWeeks.value.map(w => summaryValue(w, 'no_show')) },
      { name: 'Отменено', data: chartWeeks.value.map(w => summaryValue(w, 'cancelled_created') + summaryValue(w, 'not_created')) },
    ])

    const chartOptions = computed(() => ({
      chart: { stacked: true, toolbar: { show: false } },
      colors: [$themeColors.success, $themeColors.primary, $themeColors.warning, $themeColors.danger],
      plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
      xaxis: {
        categories: chartWeeks.value.map(w => shortWeekName(w.week_name)),
        labels: { rotate: -45, rotateAlways: chartWeeks.value.length > 6, trim: true },
      },
      legend: { position: 'top' },
    }))

    return {
      last30Days,
      weeks,
      chartWeeks,
      hasData,
      series,
      chartOptions,
      formatConversion,
      formatPeriod,
      shortWeekName,
    }
  },
}
</script>
